/**
 * Sprint T-16.1 · Automation Center · Atomic badges & inline formatters.
 *
 * All components are pure · props-in / JSX-out · no fetching · no side effects.
 * Kept in a single file to avoid 10 microscopic files; each stays <30 LOC.
 */
import React from "react";

// Category → color · aligns with automation/catalog.py categories
const CATEGORY_STYLE = {
  intelligence: "bg-indigo-500/15 text-indigo-500 border-indigo-500/30",
  reporting: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  system: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  marketplace: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  compliance: "bg-rose-500/15 text-rose-500 border-rose-500/30",
};

const STATUS_STYLE = {
  pending: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  running: "bg-blue-500/15 text-blue-400 border-blue-500/30 animate-pulse",
  success: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  failed: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  retry: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  dead: "bg-red-500/15 text-red-500 border-red-500/30",
  paused: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const TRIGGER_STYLE = {
  manual: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  cron: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  event: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  api: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  system: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};

const PRIORITY_STYLE = {
  low: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  normal: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  high: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  critical: "bg-red-500/15 text-red-500 border-red-500/30",
};

function Chip({ label, style, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide ${style}`}
    >
      {label}
    </span>
  );
}

export function StatusBadge({ status, testId }) {
  const s = (status || "pending").toString().toLowerCase();
  return <Chip label={s} style={STATUS_STYLE[s] || STATUS_STYLE.pending} testId={testId} />;
}

export function CategoryBadge({ category, testId }) {
  const c = (category || "system").toString().toLowerCase();
  return <Chip label={c} style={CATEGORY_STYLE[c] || CATEGORY_STYLE.system} testId={testId} />;
}

export function TriggerBadge({ trigger, testId }) {
  const t = (trigger || "cron").toString().toLowerCase();
  return <Chip label={t} style={TRIGGER_STYLE[t] || TRIGGER_STYLE.cron} testId={testId} />;
}

export function PriorityBadge({ priority, testId }) {
  const p = (priority || "normal").toString().toLowerCase();
  return <Chip label={p} style={PRIORITY_STYLE[p] || PRIORITY_STYLE.normal} testId={testId} />;
}

export function RetryBadge({ attempt, max, testId }) {
  const n = Number(attempt || 0);
  if (n <= 1) return null;
  const overLimit = max && n >= Number(max);
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[9px] ${
        overLimit ? "bg-red-500/15 text-red-500 border-red-500/30" : "bg-amber-500/15 text-amber-500 border-amber-500/30"
      }`}
      title={max ? `attempt ${n} of ${max}` : `attempt ${n}`}
    >
      {max ? `${n}/${max}` : `${n}×`}
    </span>
  );
}

export function DurationBadge({ ms, testId }) {
  if (ms == null) return <span data-testid={testId} className="text-[10px] text-[var(--ag-text-dim)]">—</span>;
  const n = Number(ms);
  let text;
  if (n < 1000) text = `${n}ms`;
  else if (n < 60000) text = `${(n / 1000).toFixed(1)}s`;
  else text = `${Math.floor(n / 60000)}m ${Math.floor((n % 60000) / 1000)}s`;
  return (
    <span data-testid={testId} className="font-mono text-[10px] tabular-nums text-[var(--ag-text-dim)]">
      {text}
    </span>
  );
}
